<template>
  <b-modal
    :id="modalId"
    title="Add event"
    size="lg"
    hide-footer
    no-close-on-backdrop
    @hide="onAddEntryHide"
    @hidden="onAddEntryHidden"
  >
    <div v-if="addEntryLoading" class="od-add-loading">
      <i class="fa fa-spinner fa-spin"></i>&nbsp;Loading form...
    </div>
    <div v-else-if="addEntrySuccess" class="od-add-success">
      <i class="fa fa-check-circle"></i>
      <div class="od-add-success-headline">Entry added</div>
      <div class="od-add-success-body">
        Event #{{ addEntrySuccess.event_id }} logged.
      </div>
      <div class="od-add-actions">
        <b-button variant="link" @click="close">Done</b-button>
        <b-button variant="primary" @click="addAnother">Add another</b-button>
        <b-button variant="outline-secondary" @click="openCreatedEvent">Open event</b-button>
      </div>
    </div>
    <div v-else>
      <div class="od-add-type-row">
        <button v-for="t in addEntryTypeOptions" :key="t.value"
                type="button"
                :class="['od-add-type-btn', { 'od-add-type-btn--active': addEntry.entry_type === t.value }]"
                @click="setAddEntryType(t.value)">
          <i class="fa" :class="t.icon"></i>&nbsp;{{ t.label }}
        </button>
      </div>

      <div class="od-add-field" v-if="addEntryCategoryOptions.length">
        <label for="od-add-category">Category <span class="od-add-req">*</span></label>
        <b-form-select id="od-add-category" v-model="addEntry.category" :options="addEntryCategoryOptions" size="sm"/>
      </div>

      <!-- FI-1820: sos inline on breakdown/accident. opens in a new
           tab so the half-typed entry isn't lost. -->
      <div v-if="showAddEntrySos" class="od-add-sos">
        <span class="od-add-sos-text"><strong>Need roadside help?</strong> You can send an SOS while you log this.</span>
        <router-link :to="addEntrySosLinkTo" target="_blank" class="od-add-sos-btn">
          <i class="fa fa-exclamation-triangle"></i>&nbsp;Open Bus SOS
        </router-link>
      </div>

      <!-- FI-1933 severity: incident-only (kudos / notes are always low),
           pre-set to the category default and re-seeded when the category
           changes; staff can override before submit. -->
      <div class="od-add-field" v-if="addEntry.entry_type === 'incident'">
        <label for="od-add-severity">Severity</label>
        <b-form-select id="od-add-severity" v-model="addEntry.severity" :options="severityEditOptions" size="sm"/>
      </div>

      <!-- category-driven fields, grouped in the same tinted panel
           the public form uses so the block that changes with the
           category reads as one unit. -->
      <div v-if="addEntryConditionalFields.length" class="od-add-conditional">
        <div v-for="f in addEntryConditionalFields" :key="f.key" class="od-add-field">
          <label :for="'od-add-f-' + f.key">{{ f.label }}<span v-if="f.required" class="od-add-req"> *</span></label>
          <div v-if="f.help" class="od-add-help">{{ f.help }}</div>
          <b-form-textarea v-if="f.type === 'textarea'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" :rows="2" max-rows="6" no-resize :placeholder="f.placeholder || ''"/>
          <b-form-select v-else-if="f.type === 'select'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" :options="selectOptions(f)" size="sm"/>
          <b-form-checkbox v-else-if="f.type === 'checkbox'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" size="sm">{{ f.placeholder || 'Yes' }}</b-form-checkbox>
          <b-form-input v-else-if="f.type === 'number'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="number" size="sm" :placeholder="f.placeholder || ''"/>
          <b-form-input v-else-if="f.type === 'tel'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="tel" size="sm" :placeholder="f.placeholder || ''"/>
          <b-form-input v-else-if="f.type === 'email'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="email" size="sm" :placeholder="f.placeholder || ''"/>
          <b-form-input v-else-if="f.type === 'date'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="date" size="sm"/>
          <b-form-input v-else :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" size="sm" :placeholder="f.placeholder || ''"/>
        </div>
      </div>

      <div class="od-add-field">
        <label for="od-add-what">What happened <span class="od-add-req">*</span></label>
        <b-form-textarea id="od-add-what" v-model="addEntry.what_happened" :rows="3" max-rows="10" no-resize placeholder="Brief description of the event..."/>
      </div>

      <!-- dispatch usually files an event the driver already relayed and
           often knows how it was handled; carrying the note on create
           saves reopening the event just to add it. staff modal only --
           the public form never shows this. -->
      <div class="od-add-field">
        <label for="od-add-follow-up">Follow-up &amp; resolution</label>
        <b-form-textarea id="od-add-follow-up" v-model="addEntry.follow_up_resolution" :rows="2" max-rows="8" no-resize placeholder="What was done about it, if anything yet..."/>
      </div>

      <div class="od-add-row">
        <div class="od-add-col">
          <label for="od-add-driver">Driver</label>
          <b-form-input id="od-add-driver" v-model="addEntry.driver_id" size="sm" placeholder="ID or name"/>
        </div>
        <div class="od-add-col">
          <label for="od-add-vehicle">Vehicle</label>
          <b-form-input id="od-add-vehicle" v-model="addEntry.vehicle_id" size="sm" placeholder="ID"/>
        </div>
        <div class="od-add-col">
          <label for="od-add-charter">Charter ref</label>
          <b-form-input id="od-add-charter" v-model="addEntry.charter_ref" size="sm" placeholder="Optional"/>
        </div>
      </div>

      <div class="od-add-field">
        <label for="od-add-location">Location</label>
        <b-form-input id="od-add-location" v-model="addEntry.location_text" size="sm" placeholder="Optional"/>
      </div>

      <div class="od-add-field">
        <label>Photos</label>
        <od-photo-input
          ref="addEntryPhotoInput"
          :presign="presignPhoto"
          @input="addEntryPhotoKeys = $event"
          @busy="addEntryPhotosBusy = $event"
        />
      </div>

      <div v-if="addEntryError" class="od-add-error">{{ addEntryError }}</div>

      <div class="od-add-actions">
        <b-button v-if="addEntryDirty" variant="link" class="od-add-discard" :disabled="addEntrySubmitting" @click="resetAddEntryForm">Discard draft</b-button>
        <b-button variant="link" :disabled="addEntrySubmitting" @click="close">Cancel</b-button>
        <b-button variant="primary" :disabled="!canSubmitAddEntry || addEntrySubmitting || addEntryPhotosBusy" @click="submitAddEntry">
          <i class="fa fa-plus"></i>&nbsp;<span v-if="addEntrySubmitting">Saving...</span><span v-else-if="addEntryPhotosBusy">Uploading photos...</span><span v-else>Add entry</span>
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
// FI-1815 "+ Add entry", lifted out of ops_desk.vue so the Event Log and the
// ERM Board offer the same form rather than two that drift apart. The whole
// lifecycle lives here — schema fetch and cache, the category-driven
// conditional fields, severity seeding, photo staging, and the discard
// guard. The host supplies the tenant, says whether SOS is on, and hears
// about a created event; it owns nothing else.
//
// Open it with `this.$refs.<ref>.open()`.
import OdPhotoInput from './od_photo_input.vue'
import {
  get_ops_desk_form_schema,
  create_ops_desk_event,
  presign_ops_desk_attachment,
} from '../networking/apis'

const EMPTY_ENTRY = () => ({
  entry_type: 'incident',
  category: '',
  severity: 'normal',
  what_happened: '',
  follow_up_resolution: '',
  driver_id: '',
  vehicle_id: '',
  charter_ref: '',
  location_text: '',
  extras: {},
})

export default {
  name: 'od_add_entry_modal',
  components: { OdPhotoInput },
  props: {
    tenant: { type: String, default: '' },
    // FI-1820: surfaces the inline Bus SOS prompt on breakdown / accident.
    // the host owns the feature check, since it already reads tenant features.
    sosEnabled: { type: Boolean, default: false },
    // distinct per host so two mounted copies could never collide on the id
    modalId: { type: String, default: 'add-entry-modal' },
  },
  data() {
    return {
      // schemas are fetched on first open and cached so re-opens don't pay
      // another round-trip.
      addEntrySchemaLoaded: false,
      addEntryLoading: false,
      addEntrySubmitting: false,
      addEntryError: '',
      addEntrySuccess: null,
      addEntrySchemas: { incident: {}, kudos: {}, note: {} },
      addEntryAvailableCategories: { incident: [], kudos: [], note: [] },
      // populated from the schema fetch so the severity select can pre-fill
      // from the category default without re-deriving the map.
      severityDefaults: {},
      severityEditOptions: [
        { value: 'high', text: 'High' },
        { value: 'normal', text: 'Normal' },
        { value: 'low', text: 'Low' },
      ],
      addEntryTypeOptions: [
        { value: 'incident', label: 'Issue', icon: 'fa-exclamation-circle' },
        { value: 'kudos',    label: 'Kudos',    icon: 'fa-star' },
        { value: 'note',     label: 'Note',     icon: 'fa-sticky-note-o' },
      ],
      addEntry: EMPTY_ENTRY(),
      // FI-1812 photos: the staged keys ride the create payload.
      addEntryPhotoKeys: [],
      addEntryPhotosBusy: false,
    }
  },
  computed: {
    showAddEntrySos() {
      return this.sosEnabled
        && this.addEntry.entry_type === 'incident'
        && (this.addEntry.category === 'breakdown' || this.addEntry.category === 'accident')
    },
    addEntrySosLinkTo() {
      const q = {}
      if (this.addEntry.vehicle_id) q.vehicle = this.addEntry.vehicle_id
      if (this.addEntry.location_text) q.notes = this.addEntry.location_text
      return { name: 'sos_list', query: q }
    },
    addEntryDirty() {
      const e = this.addEntry
      if (!e) return false
      if (e.category || (e.what_happened && e.what_happened.trim())) return true
      if (e.follow_up_resolution && e.follow_up_resolution.trim()) return true
      if (e.driver_id || e.vehicle_id || e.charter_ref || e.location_text) return true
      const extras = e.extras || {}
      return Object.keys(extras).some(k => extras[k] !== '' && extras[k] !== null && extras[k] !== undefined && extras[k] !== false)
    },
    addEntryCategoryOptions() {
      const t = this.addEntry.entry_type
      const cats = (this.addEntryAvailableCategories && this.addEntryAvailableCategories[t]) || []
      return [
        { value: '', text: t === 'note' ? '(No category needed)' : 'Pick a category...', disabled: t !== 'note' },
        ...cats.map(c => ({ value: c, text: this.readableLabel(c) })),
      ]
    },
    // per-(type, category) fields from the schema payload. the api
    // pre-merges entry-type-level fields into every category bucket, so
    // a category hit already carries them; _default covers the
    // no-category case (kudos before a pick, note always). mirrors the
    // public form's conditionalFields.
    addEntryRawConditionalFields() {
      const t = this.addEntry.entry_type
      const c = this.addEntry.category
      const bucket = (this.addEntrySchemas && this.addEntrySchemas[t]) || {}
      return (c && bucket[c]) || bucket._default || []
    },
    // show_if gates a follow-up field on a sibling's current value
    // (component "Other" -> "which component?"). hiding also DELETES the
    // typed value (watcher below): the server keeps non-empty gated values
    // for mobile clients, so the web must not submit a stale one.
    addEntryConditionalFields() {
      return this.addEntryRawConditionalFields.filter(
        f => !f.show_if || this.addEntry.extras[f.show_if.field] === f.show_if.value)
    },
    canSubmitAddEntry() {
      const t = this.addEntry.entry_type
      const needsCategory = t !== 'note'
      if (needsCategory && !this.addEntry.category) return false
      if (!this.addEntry.what_happened || !this.addEntry.what_happened.trim()) return false
      for (const f of this.addEntryConditionalFields) {
        if (!f.required) continue
        const v = this.addEntry.extras[f.key]
        if (v === undefined || v === null || v === '') return false
      }
      return true
    },
  },
  watch: {
    // seed select extras to '' so the placeholder option displays as
    // selected; an undefined v-model matches no option and the control
    // renders blank. also prune values of gated fields that just hid, so a
    // typed-then-rehidden value never rides the create payload.
    addEntryConditionalFields(fields) {
      (fields || []).forEach(f => {
        if (f.type === 'select' && this.addEntry.extras[f.key] === undefined) {
          this.$set(this.addEntry.extras, f.key, '')
        }
      })
      const visible = new Set((fields || []).map(f => f.key))
      this.addEntryRawConditionalFields.forEach(f => {
        if (f.show_if && !visible.has(f.key) && this.addEntry.extras[f.key] !== undefined) {
          this.$delete(this.addEntry.extras, f.key)
        }
      })
    },
    // FI-1933 re-seed the severity select to the category default whenever the
    // entry type or category changes; a manual override holds until the next
    // change. mirrors the extras-seeding pattern above.
    'addEntry.category'() { this.seedAddEntrySeverity() },
    'addEntry.entry_type'() { this.seedAddEntrySeverity() },
    // per-tenant schemas and categories: a tenant switch must drop the cache
    // and the half-typed draft, or the form would offer another fleet's
    // categories and file the entry against the wrong tenant.
    tenant() {
      this.addEntrySchemaLoaded = false
      this.addEntrySchemas = { incident: {}, kudos: {}, note: {} }
      this.addEntryAvailableCategories = { incident: [], kudos: [], note: [] }
      this.severityDefaults = {}
      this.addEntrySuccess = null
      this.resetAddEntryForm()
    },
  },
  methods: {
    readableLabel(v) {
      if (!v) return '-'
      return String(v).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    // the form is NOT reset on open: a half-filled draft survives close/reopen
    // and clears only after a successful submit or an explicit discard.
    async open() {
      if (!this.tenant) return
      this.addEntryError = ''
      this.addEntrySuccess = null
      this.$bvModal.show(this.modalId)
      if (this.addEntrySchemaLoaded) return
      this.addEntryLoading = true
      try {
        const res = await get_ops_desk_form_schema(this.tenant)
        const data = (res && res.value && res.value.data) || {}
        this.addEntryAvailableCategories = data.categories || { incident: [], kudos: [], note: [] }
        // the payload nests the per-type buckets one level down, under
        // schemas.entry_types (see field_schemas.all_schemas)
        this.addEntrySchemas = (data.schemas && data.schemas.entry_types) || { incident: {}, kudos: {}, note: {} }
        this.severityDefaults = data.severity_defaults || {}
        this.addEntrySchemaLoaded = true
        // seed the severity select now the map is available (the modal opened
        // on incident with no category, so this lands 'normal').
        this.seedAddEntrySeverity()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('od_add_entry_modal schema load failed', e)
        this.addEntryError = 'Could not load the form. Try again in a moment.'
      } finally {
        this.addEntryLoading = false
      }
    },

    close() {
      this.$bvModal.hide(this.modalId)
    },

    // the category default for the severity select. kudos / notes are always
    // low; incidents read the server-provided map, defaulting to normal.
    defaultSeverityFor(entryType, category) {
      if (entryType === 'kudos' || entryType === 'note') return 'low'
      return (this.severityDefaults && this.severityDefaults[category]) || 'normal'
    },
    seedAddEntrySeverity() {
      this.addEntry.severity = this.defaultSeverityFor(this.addEntry.entry_type, this.addEntry.category)
    },

    // esc / header-x can eat a half-typed entry on one keystroke, so a
    // dirty form asks first; confirming discards it. the footer Cancel
    // closes without a prompt and keeps the draft for the next open.
    onAddEntryHide(evt) {
      const trigger = evt && evt.trigger
      if ((trigger === 'esc' || trigger === 'headerclose') && this.addEntryDirty) {
        // always stop this close, then decide once the key event has settled.
        // calling window.confirm() synchronously inside the Esc keydown lets
        // Safari's trailing Esc dismiss the native confirm the instant it
        // opens (it flashes and vanishes), so defer it to a macrotask that
        // runs after the keyup. the guard stops a repeated Esc from queuing
        // several confirms.
        evt.preventDefault()
        if (this._addEntryConfirming) return
        this._addEntryConfirming = true
        setTimeout(() => {
          this._addEntryConfirming = false
          if (window.confirm('Discard unsaved changes?')) {
            this.resetAddEntryForm()
            this.$bvModal.hide(this.modalId)
          }
        }, 0)
      }
    },

    onAddEntryHidden() {
      // flags only; the draft itself persists (see open()).
      this.addEntryError = ''
      this.addEntrySubmitting = false
    },

    addAnother() {
      this.addEntrySuccess = null
      this.resetAddEntryForm()
    },

    openCreatedEvent() {
      const eid = this.addEntrySuccess && this.addEntrySuccess.event_id
      this.$bvModal.hide(this.modalId)
      if (!eid) return
      // the host decides where an event opens — the Event Log stays put, the
      // board stamps ?from=board so the detail's back arrow returns there.
      this.$emit('open-event', eid)
    },

    setAddEntryType(t) {
      if (this.addEntry.entry_type === t) return
      this.addEntry.entry_type = t
      this.addEntry.category = ''
      // wipe extras because schema membership changes by (type, category);
      // a stale value would survive into a different field's slot.
      this.addEntry.extras = {}
    },

    selectOptions(field) {
      const opts = Array.isArray(field.options) ? field.options : []
      return [
        { value: '', text: field.placeholder || 'Choose...', disabled: !!field.required },
        ...opts.map(o => ({ value: o.value, text: o.label || o.value })),
      ]
    },

    resetAddEntryForm() {
      this.addEntry = EMPTY_ENTRY()
      this.addEntryPhotoKeys = []
      if (this.$refs.addEntryPhotoInput) this.$refs.addEntryPhotoInput.clear()
    },

    // FI-1812 photo staging: the browser POSTs straight to s3, dodging the
    // api-gateway payload cap, and the staged keys ride the create payload.
    async presignPhoto(file) {
      const res = await presign_ops_desk_attachment(this.tenant, {
        filename: file.name,
        content_type: file.type,
        byte_size: file.size,
      })
      return (res && res.value && res.value.data) || null
    },

    async submitAddEntry() {
      if (!this.canSubmitAddEntry || this.addEntrySubmitting) return
      this.addEntryError = ''
      this.addEntrySubmitting = true
      try {
        const e = this.addEntry
        const payload = {
          entry_type: e.entry_type,
          category: e.category || (e.entry_type === 'note' ? 'note' : ''),
          what_happened: (e.what_happened || '').trim(),
          follow_up_resolution: (e.follow_up_resolution || '').trim() || undefined,
          driver_id: (e.driver_id || '').trim() || undefined,
          vehicle_id: (e.vehicle_id || '').trim() || undefined,
          charter_ref: (e.charter_ref || '').trim() || undefined,
          location_text: (e.location_text || '').trim() || undefined,
          // server splits schema-driven extras from first-class columns
          // (kudos_target, delay_duration_min); we just merge them in flat.
          ...e.extras,
          // after the spread so a schema field can never shadow these
          severity: e.severity || undefined,
          attachment_keys: this.addEntryPhotoKeys.length ? this.addEntryPhotoKeys : undefined,
        }
        const res = await create_ops_desk_event(this.tenant, payload)
        const data = (res && res.value && res.value.data) || {}
        this.addEntrySuccess = data
        // the draft is consumed once the entry lands
        this.resetAddEntryForm()
        // the host refreshes whatever it is showing, so the new entry appears
        // without a manual reload.
        this.$emit('created', data)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('od_add_entry_modal submit failed', err)
        // api errors are a flat {message} body; keep the error_data
        // path as a fallback for wrapped shapes.
        const body = err && err.response && err.response.data
        const msg = body && (body.message
          || (body.error_data && (body.error_data.error_message || body.error_data.message)))
        this.addEntryError = msg || 'Could not save the entry.'
      } finally {
        this.addEntrySubmitting = false
      }
    },
  },
}
</script>

<style scoped>
/* FI-1815 "+ Add entry" modal. compact single-column form with three big
   entry-type buttons up top, then category + conditional fields, then
   always-on context fields. tight spacing so the modal stays under-fold
   on a 13" laptop with the most common configurations. */
.od-add-loading { padding: 24px; text-align: center; color: #6b7280; font-size: 13.5px; }
.od-add-type-row {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px;
  margin-bottom: 14px;
}
.od-add-type-btn {
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 6px;
  padding: 10px 8px; font-size: 13px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .15s, border-color .15s, color .15s;
}
.od-add-type-btn:hover { background: #eef4fb; border-color: #b8c8da; }
.od-add-type-btn:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}
.od-add-type-btn--active {
  background: var(--fi-ops-accent); border-color: var(--fi-ops-accent); color: #fff;
}
.od-add-type-btn--active:hover { background: var(--fi-ops-accent-strong); border-color: var(--fi-ops-accent-strong); }

.od-add-field { margin-bottom: 12px; }
.od-add-field label {
  display: block; margin-bottom: 4px;
  font-size: 12px; font-weight: 700; color: #4b5563;
  text-transform: uppercase; letter-spacing: 0.04em;
}
.od-add-help { font-size: 12px; color: #6b7280; margin: -2px 0 4px 0; }
.od-add-req { color: #b91c1c; font-weight: 700; }

/* tinted panel for the category-driven fields; mirrors the public
   form's .op-public-section--conditional treatment */
.od-add-conditional {
  background: #f8fafc; border: 1px solid #e1e8f0;
  border-left: 3px solid var(--fi-rail-accent);
  border-radius: 6px; padding: 12px 14px 2px 14px;
  margin-bottom: 12px;
}

.od-add-row {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px;
  margin-bottom: 12px;
}
.od-add-col label {
  display: block; margin-bottom: 4px;
  font-size: 12px; font-weight: 700; color: #4b5563;
  text-transform: uppercase; letter-spacing: 0.04em;
}

.od-add-error {
  background: #fef2f2; border: 1px solid #fecaca;
  color: #b91c1c; border-radius: 4px;
  padding: 8px 10px; font-size: 13px; margin: 8px 0 12px 0;
}
.od-add-actions {
  display: flex; gap: 8px; justify-content: flex-end;
  margin-top: 14px; padding-top: 12px; border-top: 1px solid #eef0f3;
}
/* the draft-clearing link sits apart from the close/submit pair */
.od-add-discard { margin-right: auto; padding-left: 0; }

.od-add-success { text-align: center; padding: 12px 8px 4px 8px; }
.od-add-success .fa-check-circle { font-size: 38px; color: #16a34a; margin-bottom: 8px; }
.od-add-success-headline { font-size: 16px; font-weight: 600; color: #1d1d1f; margin-bottom: 4px; }
.od-add-success-body { font-size: 13px; color: #4b5563; margin-bottom: 14px; }
.od-add-success .od-add-actions { justify-content: center; border-top: none; padding-top: 4px; }

.od-add-sos {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
  margin: 0 0 12px; padding: 9px 12px;
  border: 1px solid #f0d9a8; border-radius: 6px; background: #fdf6e7;
  font-size: 12.5px; color: #6b5518;
}
.od-add-sos-text strong { color: #8a6d1a; }
.od-add-sos-btn {
  white-space: nowrap; padding: 5px 10px; border-radius: 5px;
  background: #b7791f; color: #fff !important;
  font-size: 12px; font-weight: 600; text-decoration: none !important;
}
.od-add-sos-btn:hover { background: #9c6410; }

@media (max-width: 600px) {
  .od-add-type-row { grid-template-columns: 1fr; }
  .od-add-row { grid-template-columns: 1fr; }
}
</style>
